﻿import {inject} from 'vue'
import {MarkerData} from '@/interfaces/MapsViewInterface.ts'

const getTranslation = ( key: string, defaultValue: string = '' ) => {
	const settings = inject('settings')
	const translations = settings.translations

	const translationEntry = translations.find(translation => translation.Key === key)
	if (translationEntry) {
		return translationEntry.Value ? translationEntry.Value : translationEntry.DefaultValue
	}
	console.warn(`Missing translation - "${key}"`)
	return defaultValue ? defaultValue : key
}

// Get website settings
const getSettings = () => {
	return inject('settings')
}

// Get Navigation
const getNavigation = () => {
	return inject('navigation')
}

// Scroll to an element
// Link to the anchor with a ref, e.g. @click="goto($refs.productSpecifications)"
const goTo = (element: string) => {
	const el = element
	if (el) {
		el.scrollIntoView({ behavior: 'smooth' })
	}
}

// Read more functionality
const readMore = (element: HTMLElement) => {
	console.log(element);
	const readMoreContainer: HTMLElement | null = element.querySelector('.js-read-more-container')

	if (!readMoreContainer) return

	readMoreContainer.style.maxHeight = '9999px'
	readMoreContainer.style.transition = 'max-height 400ms ease-in-out'

	const readMoreFade: HTMLElement | null = element.querySelector('.js-read-more-fade')
	if (readMoreFade) {
		readMoreFade.style.display = 'none'
	}

}

// Get unique guid
const getGuid = () => {
	function s4() {
		return Math.floor((1 + Math.random()) * 0x10000)
			.toString(16)
			.substring(1)
	}
	return s4() + s4() + '-' + s4() + '-' + s4() + '-' + s4() + '-' + s4() + s4() + s4()
}

// Get user location based on the Geolocation API and pass it to Google Maps API to get the postal code
const getUserLocation = async () => {
	if (!navigator.geolocation) {
		console.log('Geolocation is not supported by your browser');
	} else {
		const { Geocoder } = await google.maps.importLibrary("geocoding")
		navigator.geolocation.getCurrentPosition((position) => {
			const lat = position.coords.latitude;
			const long = position.coords.longitude;

			// Hit internal endpoint to log successful location fetch
			fetch('/locationlog.aspx?accept=true').then(() => {
				console.log('Location true logged');
			})


			// Get the postal code from the user's location
			// new Geocoder().geocode({ location: { lat, lng: long } }, (results, status) => {
			// 	if (status == google.maps.GeocoderStatus.OK) {
			// 		if (results[0]) {
			// 			console.log(results[0].address_components.find((c) => c.types.includes('postal_code')).long_name)
			// 		}
			// 	} else {
			// 		console.log('Geocoder failed due to: ' + status);
			// 	}
			// });
		}, () => {
			console.log('Unable to retrieve your location');

			fetch('/locationlog.aspx?accept=false').then(() => {
				console.log('Location false logged');
			})
		});
	}
}

const setShop = async (dealer: MarkerData, isCheckout: Boolean) => {
	let dealerName
	if (dealer.MenuText) {
		dealerName = dealer.MenuText
	} else {
		dealerName = dealer
	}
	return await fetch(`/mobler-api/v2/shops/setshop?ShopName=${dealerName}&IsAboutUsPage=false${isCheckout ? '&IsCheckoutPage=true' : ''}`)
		.then(response => {
			if (!response.ok) {
				throw new Error('Network response was not ok')
			}
			return response.json()
		})
		.catch(error => {
			console.error('There has been a problem with your fetch operation:', error)
			throw error
		})
}

const setLogo = () => {
	inject('settings')

	let shopSettings = getSettings()

	let mobileLogoPath
	let logoPath
	let serviceText

	let shopname = shopSettings?.shopname?.toLowerCase()
	if (shopname && shopname != "møblér" && shopname != "mobler" && shopname != "m%c3%b8bl%c3%a9r") {
		mobileLogoPath = `/Files/Templates/Designs/Mobler/dist/logos/mobler-${shopname.replace(/ /g, '')}-mobile.svg`
		logoPath = `/Files/Templates/Designs/Mobler/dist/logos/mobler-${shopname.replace(/ /g, '')}.svg`

		if (!shopSettings?.isLocalshop) {
			serviceText = getTranslation('LogoServiceText')
		}
	} else {
		logoPath = "/Files/Templates/Designs/Mobler/dist/logos/mobler.svg"
		mobileLogoPath = "/Files/Templates/Designs/Mobler/dist/logos/mobler.svg"
	}

	return {mobileLogoPath, logoPath, serviceText}
}

const determineUspBoxWidth = (uspWidth = '', uspWidthFallback = 'col-span-1', productListIndex = 0) => {
	if (uspWidth) {
		switch (uspWidth) {
			case '1-5':
				return 'col-span-1';
			case '2-5':
				return 'col-span-1 md:col-span-2 lg:col-span-2 xl:col-span-2 2xl:col-span-2';
			case '3-5':
				return 'col-span-1 md:col-span-1 lg:col-span-3 xl:col-span-3 2xl:col-span-3';
			case '4-5':
				return 'col-span-1 md:col-span-2 lg:col-span-3 xl:col-span-4 2xl:col-span-4';
			case '5-5':
				return 'col-span-1 md:col-span-2 lg:col-span-3 xl:col-span-4 2xl:col-span-5';

			case '1-3':
				return 'col-span-1';
			case '2-3':
				return 'col-span-1 lg:col-span-2';
			case '3-3':
				return 'col-span-1 lg:col-span-3';

			case '1-4':
				return 'col-span-1';
			case '2-4':
				return 'col-span-1 lg:col-span-2';
			case '3-4':
				return 'col-span-1 lg:col-span-3';

			case '4-4':
				return 'col-span-1 lg:col-span-4';

			default:
				return uspWidthFallback;
		}
	} else {
		return uspWidthFallback;
	}
}

const determineUspImageWidth = (uspWidth = '') => {
	if (uspWidth) {
		switch (uspWidth) {
			case '1-5':
				return {width: 600, height: 375};
			case '2-5':
				return {width: 736, height: 460};
			case '3-5':
				return {width: 1600, height: 460};
			case '4-5':
				return {width: 1600, height: 460};
			case '5-5':
				return {width: 1600, height: 460};

			case '1-3':
				return {width: 600, height: 375};
			case '2-3':
				return {width: 736, height: 460};
			case '3-3':
				return {width: 1600, height: 460};

			case '1-4':
				return {width: 600, height: 375};
			case '2-4':
				return {width: 736, height: 460};
			case '3-4':
				return {width: 1600, height: 460};
			case '4-4':
				return {width: 1600, height: 460};
			default:
				return {width: 600, height: 375};
		}
	} else {
		return {width: 600, height: 375};
	}
}

const getCurrentBreakPoint = () =>{
	const breakpoints = {
		sm: 640,
		md: 768,
		lg: 1024,
		xl: 1280,
		'2xl': 1536,
	};
	const width = window.innerWidth;

	if (width >= breakpoints['2xl']) {
		return '2xl';
	} else if (width >= breakpoints.xl) {
		return 'xl';
	} else if (width >= breakpoints.lg) {
		return 'lg';
	} else if (width >= breakpoints.md) {
		return 'md';
	} else {
		return 'sm';
	}
}

const getCurrentProductListColSize = () =>{
	const cols = {
		sm: 1,
		md: 2,
		lg: 3,
		xl: 4,
		'2xl': 5,
	};
	const breakPoint = getCurrentBreakPoint();
	return cols[breakPoint];
}

const log = (message: any, data = {}) =>{
	if(typeof message !== 'string') {
		data = message;
		message = 'Logging data...:'
	}
	console.log(`%c ${message} `, 'background: #663399; color: #f1f1f1', data);
}

const getShopStatus = (hours, returnType) => {
	const dayMap = {
		"Søn": 0, "Søndag": 0,
		"Man": 1, "Mandag": 1,
		"Tirs": 2, "Tirsdag": 2,
		"Ons": 3, "Onsdag": 3,
		"Tors": 4, "Torsdag": 4,
		"Fre": 5, "Fredag": 5,
		"Lør": 6, "Lørdag": 6
	};
	const now = new Date();
	const today = now.getDay();
	const currentMinutes = now.getHours() * 60 + now.getMinutes();

	let upcomingOpen = null;

	for (let i = 0; i < 7; i++) { // check today + next 6 days
		const checkDay = (today + i) % 7;

		for (const h of hours) {
			// Split multiple days (e.g. "Man, Tirs")
			const daysStr = h.columnOne.split(',').map(s => s.trim());
			let days = [];

			for (const dayStr of daysStr) {
				if (dayStr.includes('-')) {
					const [startDay, endDay] = dayStr.split('-').map(d => d.trim());
					const start = dayMap[startDay];
					const end = dayMap[endDay];
					if (start <= end) {
						for (let d = start; d <= end; d++) days.push(d);
					} else {
						for (let d = start; d <= 6; d++) days.push(d);
						for (let d = 0; d <= end; d++) days.push(d);
					}
				} else {
					days.push(dayMap[dayStr]);
				}
			}

			if (!days.includes(checkDay)) continue;

			const [startTime, endTime] = h.columnTwo.split('-').map(s => s.trim());
			const [startHour, startMin] = startTime.split(':').map(Number);
			const [endHour, endMin] = endTime.split(':').map(Number);

			const startMinutes = startHour * 60 + startMin;
			const endMinutes = endHour * 60 + endMin;

			if (i === 0) { // today
				if (currentMinutes >= startMinutes && currentMinutes <= endMinutes) {
					if (returnType === 'long') {
						return `<span class="text-primary font-bold">Åben</span> &middot; Lukker ${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`;
					} else {
						return `Lukker ${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`;
					}
				} else if (currentMinutes < startMinutes && upcomingOpen === null) {
					upcomingOpen = { hour: startHour, min: startMin, day: checkDay, dayName: daysStr[0] };
				}
			} else if (upcomingOpen === null) {
				upcomingOpen = { hour: startHour, min: startMin, day: checkDay, dayName: daysStr[0] };
			}
		}
	}

	if (upcomingOpen) {
		const dayNames = ["Søndag", "Mandag", "Tirsdag", "Onsdag", "Torsdag", "Fredag", "Lørdag"];
		const dayDiff = (upcomingOpen.day - today + 7) % 7;
		let dayText = "";
		if (dayDiff === 1) {
			dayText = "i morgen";
		} else if (dayDiff === 0) {
			dayText = "i dag";
		} else {
			dayText = `på ${dayNames[upcomingOpen.day]}`;
		}
		if (returnType === 'long') {
			return `<span class="text-black font-bold">Lukket</span> &middot; Åbner ${dayText} ${String(upcomingOpen.hour).padStart(2, '0')}:${String(upcomingOpen.min).padStart(2, '0')}`;
		} else {
			return `Åbner ${dayText} ${String(upcomingOpen.hour).padStart(2, '0')}:${String(upcomingOpen.min).padStart(2, '0')}`;
		}
	}

	return 'Lukket';
};

const getFocusableElements = (container) => {
	if (!container) return []
	return Array.from(
		container.querySelectorAll(
			'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]'
		)
	).filter(el => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden'))
}

const waitForElement = (selector, timeout = 10000) => {
	return new Promise((resolve, reject) => {
		const element = document.querySelector(selector);
		if (element) return resolve(element);

		const observer = new MutationObserver(() => {
			const el = document.querySelector(selector);
			if (el) {
				observer.disconnect();
				resolve(el);
			}
		});

		observer.observe(document.body, { childList: true, subtree: true });

		if (timeout) {
			setTimeout(() => {
				observer.disconnect();
				reject(new Error(`Timeout waiting for element: ${selector}`));
			}, timeout);
		}
	});
}

window.readMore = readMore

export { getTranslation, getSettings, getNavigation, goTo, getGuid, getUserLocation, readMore, setShop, setLogo, determineUspBoxWidth, log, getCurrentBreakPoint, getCurrentProductListColSize, determineUspImageWidth, getShopStatus, getFocusableElements, waitForElement }
